/**
 * B3 (vague Fluidité) — « Prévenir les entreprises » groupé.
 *
 * Après une visite ou une séance de validation : « 8 actions validées : prévenir
 * 3 entreprises ? ». Un envoi par entreprise (e-mail avec le lien « Vos actions » sans mot
 * de passe) ; la date d'envoi est enregistrée sur chaque action. Sans réponse de
 * l'architecte, les actions partent à 17 h 25 (et les urgentes sont déjà parties).
 * Une entreprise sans adresse e-mail est signalée (« Bâti Pro n'a pas d'adresse e-mail »).
 *
 * API : GET /api/chantiers/{cid}/distribution/a-prevenir
 *       POST /api/chantiers/{cid}/distribution/prevenir {entreprises?: [clé]}
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PaperPlaneTilt, Warning, CaretDown, CaretRight } from '@phosphor-icons/react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const pluriel = (n, un, plusieurs) => `${n} ${n > 1 ? plusieurs : un}`;

export default function PrevenirEntreprises({ chantierId, rafraichir = 0, onPrevenu }) {
  const [etat, setEtat] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [detail, setDetail] = useState(false);

  const charger = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/chantiers/${chantierId}/distribution/a-prevenir`, { withCredentials: true });
      setEtat(res.data);
    } catch {
      setEtat(null); // bandeau facultatif : jamais bloquant
    }
  }, [chantierId]);

  useEffect(() => { charger(); }, [charger, rafraichir]);

  if (!etat || !etat.nb_actions) return null;
  const joignables = etat.entreprises.filter((e) => !e.sans_adresse);
  const sansAdresse = etat.entreprises.filter((e) => e.sans_adresse);

  const prevenir = async () => {
    setEnvoi(true);
    try {
      const res = await axios.post(`${API_URL}/api/chantiers/${chantierId}/distribution/prevenir`, {}, { withCredentials: true });
      toast.success(res.data?.message || 'Entreprises prévenues');
      onPrevenu?.();
      await charger();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "L'envoi n'a pas abouti.");
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="bg-[#FFF8E7] border border-[#E8C872] p-3 space-y-2" data-testid="prevenir-entreprises">
      <div className="flex flex-wrap items-center gap-3">
        <PaperPlaneTilt size={18} className="text-[#9A5B13]" />
        <p className="text-sm text-[#2C2C2C] flex-1 min-w-[220px]">
          <strong>{pluriel(etat.nb_actions, 'action validée', 'actions validées')}</strong>
          {joignables.length > 0
            ? ` : prévenir ${pluriel(joignables.length, 'entreprise', 'entreprises')} ?`
            : ' : aucune entreprise joignable par e-mail.'}
          <span className="block text-xs text-[#6B6B6B]">
            Sinon, elles partent groupées à 17 h 25. Les actions urgentes sont déjà envoyées.
          </span>
        </p>
        {joignables.length > 0 && (
          <Button
            className="rounded-none bg-[#1E3A5F] hover:bg-[#2A4F7F] h-9 px-4 text-sm"
            onClick={prevenir}
            disabled={envoi}
            data-testid="prevenir-entreprises-envoyer"
          >
            {envoi ? 'Envoi…' : `Prévenir ${pluriel(joignables.length, 'entreprise', 'entreprises')}`}
          </Button>
        )}
        <button type="button" className="text-xs text-[#6B6B6B] flex items-center gap-1" onClick={() => setDetail((v) => !v)}>
          {detail ? <CaretDown size={12} /> : <CaretRight size={12} />} Détail
        </button>
      </div>
      {sansAdresse.map((e) => (
        <p key={e.cle} className="text-xs text-[#8B3A3A] flex items-center gap-1" data-testid="prevenir-entreprises-sans-adresse">
          <Warning size={14} /> {e.message || `${e.nom} n'a pas d'adresse e-mail`} : complétez sa fiche dans l'annuaire des entreprises.
        </p>
      ))}
      {detail && (
        <ul className="text-xs text-[#4A4A4A] space-y-1 pl-6 list-disc">
          {etat.entreprises.map((e) => (
            <li key={e.cle}>
              <strong>{e.nom}</strong> — {pluriel(e.nb_actions, 'action', 'actions')}
              {e.email ? ` · ${e.email}` : ''}{e.a_compte ? ' · a un compte' : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
